import { useState } from 'react';

import { covariateDropRuleLabel } from '@sae/engine';

import { disclosureConfigFor } from '../domain/defaults.js';
import { fmtCv, fmtInt, fmtNumber, fmtPct } from '../domain/format.js';
import { exportsUnlocked } from '../domain/gating.js';
import { useOpenLicenseDialog, useProjectState, useStore } from './hooks.js';
import { ExportButtons } from './ExportButtons.js';

export function Step4Diagnostics() {
  const store = useStore();
  const state = useProjectState();
  const openLicenseDialog = useOpenLicenseDialog();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const result = state.run.result;

  if (result === null) {
    const hasData = state.setup !== null;
    return (
      <div className="panel active">
        <div className="card">
          <h2>No Diagnostics Yet</h2>
          <p className="sub">
            {hasData
              ? 'Diagnostics, the publication checklist, the QC report, and the export center appear once a run completes.'
              : 'Load a data file in Step 1 and run the model in Step 2. Diagnostics, the publication checklist, the QC report, and the export center appear once a run completes.'}
          </p>
          <button
            className="btn btn-primary"
            onClick={() => store.goToStep(hasData ? 'configure' : 'upload')}
          >
            {hasData ? 'Go to Step 2' : 'Go to Step 1'}
          </button>
        </div>
      </div>
    );
  }

  const cellId =
    state.selectedCellId !== null &&
    result.cells.some((c) => c.cellId === state.selectedCellId)
      ? state.selectedCellId
      : (result.cells[0]?.cellId ?? '');
  const cell = result.cells.find((c) => c.cellId === cellId);
  const domains = result.domains.filter((d) => d.domain.cellId === cellId);
  const fit = cell?.fit;
  const diag = fit?.diagnostics;
  // Proportion-scale statewide figures stored on the cell result;
  // diag.aggregation's means are transformed-scale and never display.
  const figures = cell?.statewideFigures;
  const disclosure = disclosureConfigFor(state.config.disclosurePresetId);
  const licensed = exportsUnlocked(state.license);

  const minN =
    domains.length > 0 ? Math.min(...domains.map((d) => d.reliability.n)) : 0;
  const cvUnder = domains.filter(
    (d) => d.model !== undefined && (d.reliability.cv ?? Infinity) <= disclosure.cvFlag,
  ).length;
  const withModel = domains.filter((d) => d.model !== undefined).length;
  const suppressed = domains.filter((d) => d.status === 'suppressed');
  const excludedIssues = result.issues.filter(
    (i) => i.code === 'MISSING_DIRECT_ESTIMATE',
  );
  const flaggedResiduals = (diag?.standardizedResiduals ?? []).filter(
    (r) => r.flagged,
  );

  const checklist: { ok: boolean; text: string }[] = [
    {
      ok: minN >= disclosure.minRespondents,
      text: `Minimum n met in all ${domains.length} areas (smallest n = ${fmtInt(minN)})`,
    },
    {
      ok: cvUnder === withModel,
      text: `${cvUnder} of ${withModel} model CVs under the ${fmtPct(disclosure.cvFlag, 0)} flag threshold`,
    },
    {
      ok: suppressed.length === 0,
      text:
        suppressed.length === 0
          ? 'No suppressed areas under the preset'
          : `${suppressed.length} area${suppressed.length === 1 ? ' is' : 's are'} suppressed under the preset and will not export`,
    },
    {
      ok: true,
      text: `Methods metadata recorded: transform ${cell?.transform ?? '—'}, MSE ${cell?.mseMethod === 'bootstrap' ? 'bootstrap' : 'analytical'}${cell?.benchmarkApplied ? `, benchmarking by ${cell.benchmarkingMethod ?? ''} shift` : ''}`,
    },
    {
      ok: excludedIssues.length === 0,
      text:
        excludedIssues.length === 0
          ? 'No area was excluded from the model'
          : 'Areas with no direct estimate are excluded and named in the run report below',
    },
    {
      ok: true,
      text: `Reproducibility: seed "${result.manifest.seed}" and engine version ${result.manifest.engineVersion} recorded in the run manifest`,
    },
  ];

  const qcFindings = (state.qc ?? []).filter((r) => r.issues.length > 0);
  const qcPassed = (state.qc ?? []).length - qcFindings.length;

  return (
    <div className="panel active">
      <div className="diag-grid">
        <div className="card">
          <h2>Diagnostics</h2>
          <p className="sub">
            The checks a reviewer will ask about, on the same screen as the
            results.
            {result.cells.length > 1 &&
              ' Shown for the result set selected in Step 3.'}
          </p>
          {fit === undefined || diag === undefined ? (
            <p className="sub">
              This result set has no fitted model (too few areas), so no
              model diagnostics are available. The direct estimates are
              reported as computed.
            </p>
          ) : (
            <>
              <div className="diag-item">
                <div className="diag-head">
                  <b>Residual Normality</b>
                  <span>
                    {diag.shapiroWilk !== undefined
                      ? `Shapiro–Wilk p = ${fmtNumber(diag.shapiroWilk.pValue)}`
                      : 'Needs at least ten areas'}
                  </span>
                </div>
                <div className="meter">
                  <i
                    style={{
                      width: `${Math.round((diag.shapiroWilk?.pValue ?? 0.5) * 100)}%`,
                    }}
                  ></i>
                </div>
                <p className="diag-note">
                  {flaggedResiduals.length === 0
                    ? 'No area is an outlier on standardized residuals.'
                    : `Flagged residuals: ${flaggedResiduals.map((r) => `${r.areaId} (${fmtNumber(r.value)})`).join(', ')}.`}{' '}
                  Skewness {fmtNumber(diag.skewness)}, kurtosis{' '}
                  {fmtNumber(diag.kurtosis)}.
                </p>
              </div>
              <div className="diag-item">
                <div className="diag-head">
                  <b>Model Fit: Covariates</b>
                  <span>
                    {cell?.selectedCovariates.length ?? 0} selected
                    {diag.rSquaredFh !== undefined &&
                      ` · R² (Fay–Herriot) ${fmtNumber(diag.rSquaredFh)}`}
                  </span>
                </div>
                <div className="meter">
                  <i
                    style={{
                      width: `${Math.round((diag.rSquaredFh ?? 0.5) * 100)}%`,
                    }}
                  ></i>
                </div>
                <p className="diag-note">
                  Median shrinkage γ is {fmtNumber(diag.medianGamma)}.
                  {diag.lowShrinkage &&
                    ' Shrinkage is low: the direct estimates carry most of the weight in this fit.'}
                </p>
                {cell?.selection !== undefined && (
                  <p className="diag-note">
                    Offered: {cell.selection.offered.join(', ') || 'none'}.
                    Kept: {cell.selection.kept.join(', ') || 'intercept only'}.
                    {cell.selection.dropped.length > 0
                      ? ` Dropped: ${cell.selection.dropped
                          .map(
                            (d) =>
                              `${d.name} — ${covariateDropRuleLabel(d.rule, cell.selection!.criterion)}`,
                          )
                          .join('; ')}.`
                      : ' Nothing was dropped.'}{' '}
                    Covariate selection adds uncertainty that the reported
                    mean squared errors do not include.
                  </p>
                )}
              </div>
              <div className="diag-item">
                <div className="diag-head">
                  <b>Bias Check: Model vs. Direct</b>
                  <span>
                    {fmtPct(Math.abs(diag.aggregation.relativeDifference))}{' '}
                    apart
                  </span>
                </div>
                <div className="meter">
                  <i
                    style={{
                      width: `${Math.max(0, 100 - Math.round(Math.abs(diag.aggregation.relativeDifference) * 200))}%`,
                    }}
                  ></i>
                </div>
                <p className="diag-note">
                  {figures !== undefined && (
                    <>
                      The{' '}
                      {figures.populationWeighted
                        ? 'population-weighted model mean'
                        : 'model mean'}{' '}
                      is {fmtPct(figures.modelMean)} against a statewide
                      direct estimate of {fmtPct(figures.directEstimate)}.
                    </>
                  )}
                  {diag.aggregation.warning &&
                    ' The gap is larger than the 5% review threshold.'}
                </p>
              </div>
              <div className="code" style={{ marginTop: 16 }}>
                {`Coefficients (fitting scale)
${['Intercept', ...(cell?.selectedCovariates ?? [])]
  .map(
    (name, i) =>
      `${name.padEnd(22)} β ${fmtNumber(fit.beta[i] ?? NaN)}   SE ${fmtNumber(fit.betaStandardErrors[i] ?? NaN)}`,
  )
  .join('\n')}
A (REML)             ${fmtNumber(fit.sigma2v, 3)}   ·   method: ${fit.methodUsed}`}
              </div>
            </>
          )}
        </div>

        <div>
          <div className="card" style={{ marginBottom: 12 }}>
            <h2>Publication Checklist</h2>
            <p className="sub">
              {state.config.disclosurePresetId === 'standard'
                ? 'Standard preset'
                : state.config.disclosurePresetId === 'strict'
                  ? 'Strict preset'
                  : state.config.disclosurePresetId === 'sae-standard'
                    ? 'SAE Standard preset'
                    : 'Disclosure checks off'}{' '}
              applied to this run, for the result set in Step 3.
            </p>
            <ul className="checklist">
              {checklist.map((item, i) => (
                <li key={i} className={item.ok ? '' : 'fail'}>
                  {item.text}
                </li>
              ))}
            </ul>
          </div>

          <div className="card">
            <h2>What You Can Download{licensed ? '' : ' with a License'}</h2>
            <p className="sub">
              {licensed
                ? 'Your license is active; every export is available.'
                : 'In the trial these downloads stay locked.'}
            </p>
            <ExportButtons
              detailed
              kinds={[
                'area-report-xlsx',
                'methods-report-docx',
                'master-dataset',
                'reproducibility-zip',
                'results-csv',
              ]}
            />
            {!licensed && (
              <div className="lock-note">
                <span>🔒</span>
                <span>
                  <strong>Why locked?</strong> The trial runs the full
                  workflow on sample data and a capped preview on your own
                  data. Exports require a license.{' '}
                  <button className="linklike" onClick={openLicenseDialog}>
                    Unlock the Tool
                  </button>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <h2>QC Report</h2>
        <p className="sub">
          Every check that ran, every exclusion, and every warning. No
          finding is dropped silently.
        </p>
        <div className="grid2">
          <div>
            <h3>File and Setup Checks</h3>
            {state.source.readIssues.length > 0 && (
              <ul className="issue-list">
                {state.source.readIssues.map((issue, i) => (
                  <li key={`io-${i}`} className={issue.severity}>
                    {issue.message}
                  </li>
                ))}
              </ul>
            )}
            {state.qc === null ? (
              <p className="sub">
                Quality checks have not run on the current setup.{' '}
                <button
                  className="linklike"
                  onClick={() => void store.refreshQc()}
                >
                  Run them now
                </button>
              </p>
            ) : (
              <>
                <p className="sub">
                  {qcPassed} of {state.qc.length} checks passed with no
                  findings.
                </p>
                {qcFindings.map((check) => (
                  <div key={check.checkId} className="qc-check">
                    <b>{check.checkId}</b>
                    <ul className="issue-list">
                      {check.issues.map((issue, i) => (
                        <li key={i} className={issue.severity}>
                          {issue.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </>
            )}
          </div>
          <div>
            <h3>Run Report</h3>
            {result.issues.length === 0 ? (
              <p className="sub">The run reported no issues.</p>
            ) : (
              <ul className="issue-list">
                {result.issues.map((issue, i) => (
                  <li key={i} className={issue.severity}>
                    <b>{issue.code}</b>: {issue.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <h2>Project</h2>
        <div className="fgroup">
          <label htmlFor="project-name">Project Name</label>
          <input
            id="project-name"
            type="text"
            value={state.projectName}
            onChange={(e) => store.renameProject(e.target.value)}
          />
        </div>
        <label className="check">
          <input
            type="checkbox"
            checked={state.keepDataWithProject}
            onChange={(e) => store.setKeepDataWithProject(e.target.checked)}
          />
          <span>
            <b>Keep the Data File with the Project</b>
            <span>
              Stored only in this browser, so the project reopens exactly
              as left. If this is off, the settings and results are kept,
              but the file itself is not stored.
            </span>
          </span>
        </label>
        <p className="sub">
          The project autosaves in this browser as you work. No data,
          results, or file names are sent to a server; the only network
          call this app makes is the license check.
        </p>
        <div className="dialog-actions">
          <button className="btn" onClick={() => store.newProject()}>
            New Project
          </button>
          {confirmingDelete ? (
            <button
              className="btn btn-danger"
              onClick={() => void store.deleteAllLocalData()}
            >
              Confirm: Delete All Local Data
            </button>
          ) : (
            <button
              className="btn"
              onClick={() => setConfirmingDelete(true)}
            >
              Delete All Local Data
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
